<template>
  <div
    class="min-h-screen flex flex-col items-center justify-center py-6 px-4 sm:px-6 lg:px-8 bg-lightGray bg-cover bg-center relative overflow-hidden"
  >
    <div
      class="max-w-xl w-full space-y-8 bg-white/80 backdrop-blur-xl p-8 rounded-2xl overflow-hidden shadow-2xl shadow-cosmicPurple/10 relative z-10"
    >
      <ShineBorder
        :border-width="2"
        :duration="24"
        :shine-color="['#A07CFE', '#FE8FB5', '#A07CFE']"
      />
      <div class="relative flex items-center justify-center">
        <div class="absolute -top-[25rem] z-10 bg-[#4d7aff] w-[30rem] h-[30rem] rounded-full blur-[100px] opacity-30 appear"></div>
        <LogoMainLink class="z-20" />
      </div>

      <!-- Step Navigation -->
      <div v-if="!showSuccessMessage">
        <h2 class="text-center text-2xl text-gray-900 text-pretty font-light">
          Sign up to get started
        </h2>
        <div v-if="currentStep === 1">
          <!-- Step 1: Basic Information -->
          <Form @submit.prevent="nextStep" class="mt-8 space-y-6 max-w-[20rem] mx-auto">
            <div class="space-y-4">
              <FormField v-slot="{ componentField }" name="username">
                <FormItem>
                  <FormControl>
                    <FormLabel>Username</FormLabel>
                    <Input
                      v-bind="componentField"
                      v-model="form_data.username"
                      placeholder="Enter your username"
                      @blur="checkUsernameUniqueness"
                      required
                    />
                  </FormControl>
                  <p v-if="username_error" class="text-red-500 text-sm">
                    Username is already taken.
                  </p>
                  <p v-if="validation_errors.username" class="text-red-500 text-sm">
                    {{ validation_errors.username }}
                  </p>
                </FormItem>
              </FormField>

              <FormField v-slot="{ componentField }" name="email">
                <FormItem>
                  <FormControl>
                    <FormLabel>Email</FormLabel>
                    <Input
                      v-bind="componentField"
                      v-model="form_data.email"
                      type="email"
                      placeholder="Enter your email"
                      @blur="checkEmailUniqueness"
                      required
                    />
                  </FormControl>
                  <p v-if="email_error" class="text-red-500 text-sm">Email is already taken.</p>
                  <p v-if="validation_errors.email" class="text-red-500 text-sm">
                    {{ validation_errors.email }}
                  </p>
                </FormItem>
              </FormField>

              <FormField v-slot="{ componentField }" name="password">
                <FormItem>
                  <FormControl>
                    <FormLabel>Password</FormLabel>
                    <Input
                      v-bind="componentField"
                      v-model="form_data.password"
                      type="password"
                      placeholder="Enter your password"
                      @blur="checkPasswordStrength"
                      required
                    />
                  </FormControl>
                  <p v-if="validation_errors.password" class="text-red-500 text-sm">
                    {{ validation_errors.password }}
                  </p>
                </FormItem>
              </FormField>

              <FormField v-slot="{ componentField }" name="password_confirm">
                <FormItem>
                  <FormControl>
                    <FormLabel>Confirm password</FormLabel>
                    <Input
                      v-bind="componentField"
                      v-model="password_confirm"
                      type="password"
                      placeholder="Repeat your password"
                      @blur="checkPasswordStrength"
                      required
                    />
                  </FormControl>
                  <p v-if="validation_errors.password_confirm" class="text-red-500 text-sm">
                    {{ validation_errors.password_confirm }}
                  </p>
                </FormItem>
              </FormField>
            </div>

            <Button
              type="submit"
              class="w-full bg-cosmicPurple shadow-lg shadow-cosmicPurple/20 text-white hover:bg-cosmicPurple/90 hover:-translate-y-[1px] transition-all duration-200"
              @click="validateAndContinue"
            >
              Continue
            </Button>

            <!-- Social Login Divider -->
            <div class="relative">
              <div class="absolute inset-0 flex items-center">
                <div class="w-full border-t border-gray-300"></div>
              </div>
              <div class="relative flex justify-center text-sm">
                <span class="px-2 bg-white text-gray-500">Or continue with</span>
              </div>
            </div>

            <!-- Social Login Buttons -->
            <SocialLoginButtons />
          </Form>
        </div>

        <div v-if="currentStep === 2">
          <!-- Step 2: Additional Information -->
          <Form @submit.prevent="nextStep" class="mt-8 space-y-6 max-w-[20rem] mx-auto">
            <div class="space-y-4">
              <FormField v-slot="{ componentField }" name="first_name">
                <FormItem>
                  <FormControl>
                    <FormLabel>First name</FormLabel>
                    <Input
                      v-bind="componentField"
                      v-model="form_data.first_name"
                      placeholder="Enter your first name"
                      required
                    />
                  </FormControl>
                  <p v-if="validation_errors.first_name" class="text-red-500 text-sm">
                    {{ validation_errors.first_name }}
                  </p>
                </FormItem>
              </FormField>

              <FormField v-slot="{ componentField }" name="last_name">
                <FormItem>
                  <FormControl>
                    <FormLabel>Last name</FormLabel>
                    <Input
                      v-bind="componentField"
                      v-model="form_data.last_name"
                      placeholder="Enter your last name"
                      required
                    />
                  </FormControl>
                  <p v-if="validation_errors.last_name" class="text-red-500 text-sm">
                    {{ validation_errors.last_name }}
                  </p>
                </FormItem>
              </FormField>

              <FormField v-slot="{ componentField }" name="telegram">
                <FormItem>
                  <FormControl>
                    <FormLabel>Telegram username</FormLabel>
                    <Input
                      v-bind="componentField"
                      v-model="form_data.telegram_username"
                      placeholder="Enter your telegram username"
                      required
                    />
                  </FormControl>
                  <p v-if="validation_errors.telegram_username" class="text-red-500 text-sm">
                    {{ validation_errors.telegram_username }}
                  </p>
                </FormItem>
              </FormField>

              <FormField v-slot="{ componentField }" name="linkedin">
                <FormItem>
                  <FormControl>
                    <FormLabel>LinkedIn profile URL</FormLabel>
                    <Input
                      v-bind="componentField"
                      v-model="form_data.linkedin_profile_url"
                      placeholder="Enter your LinkedIn URL"
                      required
                    />
                  </FormControl>
                  <p v-if="validation_errors.linkedin_profile_url" class="text-red-500 text-sm">
                    {{ validation_errors.linkedin_profile_url }}
                  </p>
                </FormItem>
              </FormField>

              <FormField v-slot="{ componentField }" name="leetcode">
                <FormItem>
                  <FormControl>
                    <FormLabel>LeetCode profile URL</FormLabel>
                    <Input
                      v-bind="componentField"
                      v-model="form_data.leetcode_profile_url"
                      placeholder="Enter your LeetCode URL"
                      required
                    />
                  </FormControl>
                  <p v-if="validation_errors.leetcode_profile_url" class="text-red-500 text-sm">
                    {{ validation_errors.leetcode_profile_url }}
                  </p>
                </FormItem>
              </FormField>
            </div>

            <div class="flex flex-col gap-4">
              <Button
                type="submit"
                class="w-full bg-cosmicPurple shadow-lg shadow-cosmicPurple/20 text-white hover:bg-cosmicPurple/90 hover:-translate-y-[1px] transition-all duration-200"
                @click="validateAndContinueStep2"
              >
                Continue
              </Button>
              <button
                @click="prevStep"
                type="button"
                class="w-full text-sm bg-transparent text-gray-500 hover:text-gray-900 transition-all duration-200"
              >
                Back
              </button>
            </div>
          </Form>
        </div>

        <div v-if="currentStep === 3">
          <!-- Step 3: Final Information -->
          <Form @submit.prevent="register" class="mt-8 space-y-6 max-w-[20rem] mx-auto">
            <div class="space-y-4">
              <FormField name="country">
                <FormItem>
                  <FormLabel>Country of residence</FormLabel>
                  <FormControl>
                    <Select v-model="form_data.country_code_iso_2">
                      <SelectTrigger>
                        <SelectValue placeholder="Select a country" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem
                          v-for="country in countries"
                          :key="country.id"
                          :value="country.id"
                        >
                          {{ country.country_name }}
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </FormControl>
                  <p v-if="validation_errors.country" class="text-red-500 text-sm">
                    {{ validation_errors.country }}
                  </p>
                </FormItem>
              </FormField>

              <FormField name="gender">
                <FormItem>
                  <FormLabel>Gender</FormLabel>
                  <FormControl>
                    <Select v-model="form_data.gender">
                      <SelectTrigger>
                        <SelectValue placeholder="Select a gender" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem v-for="gender in genders" :key="gender.id" :value="gender.id">
                          {{ gender.gender_name }}
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </FormControl>
                  <p v-if="validation_errors.gender" class="text-red-500 text-sm">
                    {{ validation_errors.gender }}
                  </p>
                </FormItem>
              </FormField>

              <FormField name="job_role">
                <FormItem>
                  <FormLabel>Current job role</FormLabel>
                  <FormControl>
                    <Select v-model="form_data.current_job_role">
                      <SelectTrigger>
                        <SelectValue placeholder="Select a job role" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem v-for="role in job_roles" :key="role.id" :value="role.id">
                          {{ role.job_name }}
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </FormControl>
                  <p v-if="validation_errors.job_role" class="text-red-500 text-sm">
                    {{ validation_errors.job_role }}
                  </p>
                </FormItem>
              </FormField>

              <FormField name="bachelor_degree">
                <FormItem>
                  <FormLabel>Bachelor degree status</FormLabel>
                  <FormControl>
                    <Select v-model="form_data.bachelor_degree_status">
                      <SelectTrigger>
                        <SelectValue placeholder="Select bachelor degree status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem
                          v-for="option in bachelor_degree_options"
                          :key="option.value"
                          :value="option.value"
                        >
                          {{ option.label }}
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </FormControl>
                  <p v-if="validation_errors.bachelor_degree" class="text-red-500 text-sm">
                    {{ validation_errors.bachelor_degree }}
                  </p>
                </FormItem>
              </FormField>
            </div>

            <div class="flex gap-4 flex-col">
              <Button
                type="submit"
                @click="register"
                class="w-full bg-cosmicPurple shadow-lg shadow-cosmicPurple/20 text-white hover:bg-cosmicPurple/90 hover:-translate-y-[1px] transition-all duration-200"
              >
                Continue
              </Button>
              <button
                @click="prevStep"
                type="button"
                class="w-full text-sm bg-transparent text-gray-500 hover:text-gray-900 transition-all duration-200"
              >
                Back
              </button>
            </div>
          </Form>
        </div>
        <!-- Sign in link -->
        <div class="text-center text-sm text-gray-600 mt-4">
          Already have an account?
          <router-link to="/login" class="font-medium text-indigo-600 hover:text-indigo-500">
            Sign in
          </router-link>
        </div>
      <TermsAndConditionsLinks />
      </div>

      <!-- Success Message -->
      <div v-else class="space-y-4 text-center pb-8">
        <div class="relative mx-auto w-12 h-12">
          <Badge class="absolute top-0 left-0 w-12 h-12 text-lime-500 mx-auto spin-in" />
          <Check class="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-6 h-6 text-lime-500" />
        </div>
        <h3 class="text-xl font-semibold text-gray-900">Thanks for signing up!</h3>
        <p class="text-gray-600">Your account has been created successfully. Logging you in...</p>
        
      </div>

      <!-- Pagination Indicator -->
      <!-- <p class="text-center text-gray-500 mt-4">Page {{ currentStep }} out of 3</p> -->

      <!-- Terms and Conditions -->
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import axios from 'axios'
import { Badge, Check } from 'lucide-vue-next'
import { API_ROUTES } from '@/const/apiRoutes'
import { useAuthStore } from '@/stores/auth'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Form, FormField, FormItem, FormLabel, FormControl } from '@/components/ui/form'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select'
import ShineBorder from '@/components/ui/ShineBorder.vue'
import TermsAndConditionsLinks from '@/components/common/TermsAndConditionsLinks.vue'
import LogoMainLink from '@/components/common/LogoMainLink.vue'
import SocialLoginButtons from '@/components/auth/SocialLoginButtons.vue'

// Define types for form data and API response data
interface BachelorDegreeOption {
  value: string
  name: string
  label: string
}

interface Gender {
  id: number
  gender_name: string
}

interface JobRole {
  id: number
  job_name: string
}

interface Country {
  id: string
  country_name: string
}

interface FormData {
  first_name: string
  last_name: string
  username: string
  email: string
  linkedin_profile_url: string
  leetcode_profile_url: string
  bachelor_degree_status: string | null
  telegram_username: string
  gender: number | null
  current_job_role: number | null
  country_code_iso_2: string | null
  slack_member_id: string
  password: string
}

const router = useRouter()
const route = useRoute()
const authStore = useAuthStore()
const currentStep = ref(1) // Track current step of the form
const showSuccessMessage = ref(false) // Add success message state
const form_data = ref<FormData>({
  first_name: '',
  last_name: '',
  username: '',
  email: '',
  linkedin_profile_url: '',
  leetcode_profile_url: '',
  bachelor_degree_status: null,
  telegram_username: '',
  gender: null,
  current_job_role: null,
  country_code_iso_2: null,
  slack_member_id: '',
  password: ''
})

const password_confirm = ref('')
const username_error = ref(false)
const email_error = ref(false)
const password_error = ref(false)
const validation_errors = ref<Record<string, string>>({})
const bachelor_degree_options = ref<BachelorDegreeOption[]>([])
const genders = ref<Gender[]>([])
const job_roles = ref<JobRole[]>([])
const countries = ref<Country[]>([])

const checkPasswordStrength = () => {
  const password = form_data.value.password
  if (!password) return

  // Reset previous errors
  validation_errors.value.password = ''

  // Check password length
  if (password.length < 8) {
    validation_errors.value.password = 'Password must be at least 8 characters long'
    return
  }

  // // Check for at least one uppercase letter
  // if (!/[A-Z]/.test(password)) {
  //   validation_errors.value.password = 'Password must contain at least one uppercase letter'
  //   return
  // }

  // // Check for at least one lowercase letter
  // if (!/[a-z]/.test(password)) {
  //   validation_errors.value.password = 'Password must contain at least one lowercase letter'
  //   return
  // }

  // // Check for at least one number
  // if (!/\d/.test(password)) {
  //   validation_errors.value.password = 'Password must contain at least one number'
  //   return
  // }

  // Check for at least one special character
  // if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
  //   validation_errors.value.password = 'Password must contain at least one special character'
  //   return
  // }

  // If we get here, the password is strong
  validation_errors.value.password = ''
}

// Fetch dropdown data from API endpoints
const fetchDropdownData = async () => {
  await Promise.all([
    fetchBachelorDegreeOptions(),
    fetchGenders(),
    fetchJobRoles(),
    fetchCountries()
  ])
}

const fetchBachelorDegreeOptions = async () => {
  try {
    const response = await axios.get(API_ROUTES.GET_BACHELORS_DEGREE_OPTIONS)
    bachelor_degree_options.value = Array.isArray(response.data)
      ? response.data
      : Object.entries(response.data).map(([key, value]) => ({ value: key, name: value, label: value }))
  } catch (error) {
    console.error('Error fetching bachelor degree options:', error)
  }
}

const fetchGenders = async () => {
  try {
    const response = await axios.get(API_ROUTES.GET_GENDERS)
    genders.value = response.data.map((item: Gender) => ({
      id: item.id,
      gender_name: item.gender_name
    }))
  } catch (error) {
    console.error('Error fetching genders:', error)
  }
}

const fetchJobRoles = async () => {
  try {
    const response = await axios.get(API_ROUTES.GET_USER_JOB_ROLES)
    job_roles.value = response.data.map((item: JobRole) => ({
      id: item.id,
      job_name: item.job_name
    }))
  } catch (error) {
    console.error('Error fetching job roles:', error)
  }
}

const fetchCountries = async () => {
  try {
    const response = await axios.get(API_ROUTES.GET_COUNTRIES)
    countries.value = response.data.map((item: Country) => ({
      id: item.id,
      country_name: item.country_name
    }))
  } catch (error) {
    console.error('Error fetching countries:', error)
  }
}

const checkUsernameUniqueness = async () => {
  if (!form_data.value.username) return
  try {
    const response = await axios.post(API_ROUTES.CHECK_USERNAME_UNIQUENESS, {
      username: form_data.value.username
    })
    username_error.value = !response.data.valid
  } catch (error) {
    console.error('Error checking username uniqueness:', error)
    username_error.value = true
  }
}

const checkEmailUniqueness = async () => {
  if (!form_data.value.email) return
  try {
    const response = await axios.post(API_ROUTES.CHECK_EMAIL_UNIQUENESS, {
      email: form_data.value.email
    })
    email_error.value = !response.data.valid
  } catch (error) {
    console.error('Error checking email uniqueness:', error)
    email_error.value = true
  }
}

// eslint-disable-next-line complexity
const validateStep = (step: number): boolean => {
  validation_errors.value = {}

  if (step === 1) {
    if (!form_data.value.username) {
      validation_errors.value.username = 'Username is required'
    }
    if (!form_data.value.email) {
      validation_errors.value.email = 'Email is required'
    }
    if (!form_data.value.password) {
      validation_errors.value.password = 'Password is required'
    } else if (form_data.value.password.length < 8) {
      validation_errors.value.password = 'Password must be at least 8 characters long'
    }
    if (!password_confirm.value) {
      validation_errors.value.password_confirm = 'Please confirm your password'
    }
    if (form_data.value.password !== password_confirm.value) {
      validation_errors.value.password = 'Passwords do not match'
    }
  } else if (step === 2) {
    if (!form_data.value.first_name) {
      validation_errors.value.first_name = 'First name is required'
    }
    if (!form_data.value.last_name) {
      validation_errors.value.last_name = 'Last name is required'
    }
    if (!form_data.value.telegram_username) {
      validation_errors.value.telegram_username = 'Telegram username is required'
    }
    if (!form_data.value.linkedin_profile_url) {
      validation_errors.value.linkedin_profile_url = 'LinkedIn profile URL is required'
    }
    if (!form_data.value.leetcode_profile_url) {
      validation_errors.value.leetcode_profile_url = 'LeetCode profile URL is required'
    }
  } else if (step === 3) {
    if (!form_data.value.country_code_iso_2) {
      validation_errors.value.country = 'Country is required'
    }
    if (!form_data.value.gender) {
      validation_errors.value.gender = 'Gender is required'
    }
    if (!form_data.value.current_job_role) {
      validation_errors.value.job_role = 'Job role is required'
    }
    if (!form_data.value.bachelor_degree_status) {
      validation_errors.value.bachelor_degree = 'Bachelor degree status is required'
    }
  }

  return Object.keys(validation_errors.value).length === 0
}

const validateAndContinue = () => {
  // Clear previous validation errors
  validation_errors.value = {}

  // Validate step 1 fields
  let hasErrors = false

  if (!form_data.value.username) {
    validation_errors.value.username = 'Username is required'
    hasErrors = true
  }

  if (!form_data.value.email) {
    validation_errors.value.email = 'Email is required'
    hasErrors = true
  }

  if (!form_data.value.password) {
    validation_errors.value.password = 'Password is required'
    hasErrors = true
  } else if (form_data.value.password.length < 8) {
    validation_errors.value.password = 'Password must be at least 8 characters long'
    hasErrors = true
  }

  if (!password_confirm.value) {
    validation_errors.value.password_confirm = 'Please confirm your password'
    hasErrors = true
  }

  if (form_data.value.password !== password_confirm.value) {
    validation_errors.value.password = 'Passwords do not match'
    hasErrors = true
  }

  // Only proceed if there are no validation errors and no username/email errors
  if (!hasErrors && !username_error.value && !email_error.value) {
    currentStep.value++
  }
}

const validateAndContinueStep2 = () => {
  // Clear previous validation errors
  validation_errors.value = {}

  // Validate step 2 fields
  let hasErrors = false

  if (!form_data.value.first_name) {
    validation_errors.value.first_name = 'First name is required'
    hasErrors = true
  }

  if (!form_data.value.last_name) {
    validation_errors.value.last_name = 'Last name is required'
    hasErrors = true
  }

  if (!form_data.value.telegram_username) {
    validation_errors.value.telegram_username = 'Telegram username is required'
    hasErrors = true
  }

  if (!form_data.value.linkedin_profile_url) {
    validation_errors.value.linkedin_profile_url = 'LinkedIn profile URL is required'
    hasErrors = true
  }

  if (!form_data.value.leetcode_profile_url) {
    validation_errors.value.leetcode_profile_url = 'LeetCode profile URL is required'
    hasErrors = true
  }

  // Only proceed if there are no validation errors
  if (!hasErrors) {
    currentStep.value++
  }
}

const nextStep = () => {
  if (currentStep.value === 1) {
    validateAndContinue()
  } else if (currentStep.value === 2) {
    validateAndContinueStep2()
  } else {
    if (validateStep(currentStep.value)) {
      currentStep.value++
    }
  }
}

const prevStep = () => {
  if (currentStep.value > 1) currentStep.value--
}

const register = async () => {
  if (!validateStep(currentStep.value)) {
    return
  }

  try {
    // Prepare the data correctly
    const prepared_data = {
      ...form_data.value,
      gender: form_data.value.gender || null,
      current_job_role: form_data.value.current_job_role || null,
      country_code_iso_2: form_data.value.country_code_iso_2 || null,
      bachelor_degree_status: form_data.value.bachelor_degree_status || null
    }

    console.log('Prepared data:', prepared_data)

    // Send registration data to API
    const response = await axios.post(API_ROUTES.USER_REGISTER, prepared_data)
    console.log('Registration successful:', response.data)

    // Show success message briefly
    showSuccessMessage.value = true

    // Auto-login after successful registration
    try {
      await authStore.login({
        email: form_data.value.email,
        password: form_data.value.password
      })
      // Small delay to show success message
      await new Promise(resolve => setTimeout(resolve, 1500))
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      router.push('/')
    } catch (loginError) {
      console.warn('Auto-login failed after registration, redirecting to login:', loginError)
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      router.push('/login')
    }
  } catch (error) {
    console.error('Registration error:', error)
  }
}

// Initialize email from URL parameter if present
onMounted(() => {
  const emailParam = route.query.email as string
  if (emailParam) {
    form_data.value.email = emailParam
  }
})

// eslint-disable-next-line @typescript-eslint/no-floating-promises
fetchDropdownData()
</script>

<style scoped>
.shine-text {
  color: transparent;
  background-clip: text !important;
  animation: shine 4s linear infinite;
}

.shine-text {
  background:
    radial-gradient(circle at center, #cdbeff, transparent) -200% 50% / 200% 100% no-repeat,
    #5769ff;
}

@keyframes shine {
  0% {
    background-position: 200% 0%;
  }
  25% {
    background-position: -200% 0%;
  }
}

.appear {
  animation: appear 1.5s ease-in-out;
}

@keyframes appear {
  0% {
    opacity: 0;
    transform: scale(0.5);
  }
  100% {
    opacity: 0.3;
    transform: scale(1);
  }
}

.spin-in {
  animation: spinIn 1s forwards ease-in-out;
}

@keyframes spinIn {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(180deg);
  }
}

.input-text {
  @apply w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-cosmicPurple focus:border-transparent;
}
</style>
